import {
  getCldContactData,
  getCldUser,
  getIsPurchaseOrderRequired,
  getUser,
  saveUser,
  updateUser,
  updateUserCategories,
  updateUserEmails,
  getAvailableLicenses,
  getAvailableBrands,
} from "../api/user";
import { defineStore } from "pinia";

// import { useURLs } from "~~/constants/api.urls";
import nuxtStorage from "nuxt-storage";
import { validateNSendContact } from "~/api/turnStile";
import { useOrderStore } from "./OrderStore";
import { useCartStore } from "./CartStore";
import { useAccountStatementStore } from "./AccountStatementStore";
import { usePrepaymentProgressStore } from "./PrepaymentProgressStore";

export const useUserStore = defineStore("user", () => {
  const orderStore = useOrderStore();
  const cartStore = useCartStore();
  const accountStatementStore = useAccountStatementStore();
  const prepaymentProgressStore = usePrepaymentProgressStore();
  const isAuthenticated = ref(!!useCookie("isAuthenticated").value);
  const auth = useAuth();
  const callbackUrlResponseApi = ref(null);
  const hmac = ref(null);
  // user info On Nest Server
  const t = (key, params) => {
    if (!key) return "";
    try {
      const nuxtApp = useNuxtApp();
      const i18n = nuxtApp?.$i18n;
      if (i18n && typeof i18n.t === "function") {
        return i18n.t(key, params);
      }
    } catch {
      // No Nuxt context yet
    }
    return String(key);
  };
  const user = ref({
    _id: "",
    userId: "",
    categories: [],
    licenses: [],
    brands: [],
    tours: [],
    newsLetterSeen: false,
    meta: {
      apiKey: {
        hashedKey: "",
        prefix: "",
        name: "",
        createdAt: null,
        expiresAt: null,
        permissions: [],
        lastUsed: null,
        isActive: false,
      },
      apiKeys: [
        {
          hashedKey: "",
          prefix: "",
          name: "",
          createdAt: null,
          expiresAt: null,
          permissions: [],
          lastUsed: null,
          isActive: false,
        },
      ],
    },
  });
  const cldUser = ref({
    cemail2: "",
    cemailpdn: "",
    cemailwbo: "",
    chain: "",
    email: "",
    emailShp: "",
    id: "",
    idCompta: "",
    name: "",
    role: "",
    category: "",
    callbackUrl: "",
    hmacSign: "",
    iTerms: "",
    cfranco: 0,
    canPayOnline: false,
    factoring: false,
    roles: [],
  });

  const CLD_USER_CACHE_KEY = "cldUserCache";
  const CLD_USER_REFRESH_MS = 5 * 60 * 1000;
  const cldUserLoadedAt = ref(0);
  let cldUserLoadPromise = null;

  /**
   * SINGLE SOURCE OF TRUTH: Clears ALL user-related state, localStorage, and cookies.
   * Call this on LOGOUT and BEFORE LOGIN to prevent cross-user data leakage.
   */
  const clearAllUserState = () => {
    // 1. Clear ALL localStorage keys related to user/session
    if (process.client) {
      const keysToRemove = [
        CLD_USER_CACHE_KEY,        // cldUserCache
        "accountingSituation",
        "totalDeliverables",
        "cart",
        "cartData",
        "currentUserSession",      // user session marker for cache validation
      ];

      // Token expiry recorded by useAuth: a stale value would mistime the silent refresh
      auth.clearExpiry();
      
      for (const key of keysToRemove) {
        try {
          window.localStorage.removeItem(key);
        } catch {}
        try {
          nuxtStorage?.localStorage?.removeItem?.(key);
          nuxtStorage?.localStorage?.removeData?.(key);
        } catch {}
      }
    }

    // 2. CRITICAL: Clear Nuxt's useAsyncData cache for user-related API calls
    // This prevents stale data from being returned after re-login
    // Use prefix matching since keys now include timestamps
    try {
      clearNuxtData((key) => {
        if (typeof key !== 'string') return false;
        return key.startsWith("CLD_USER") || 
               key.startsWith("user-") || 
               key.startsWith("CLD_CONTACTS") ||
               key.includes("statement") ||
               key === "deliverable-orders" ||
               key.includes("accounting") ||
               key.includes("deliverable") ||
               (key.startsWith("api-GET-") && key.includes("user"));
      });
    } catch {}

    // 3. Clear order store state (accounting, deliverables) - call directly without silent catch
    orderStore.setAccountingSituation();

    // 4. Clear cart store state
    try {
      cartStore.resetCartStates();
    } catch {}
    // 4.5. Clear account statement store state
    try {
      accountStatementStore.resetAccountStatement();
    } catch (err) {
      console.error("Error resetting account statement store:", err);
    }
    // 4.55. Clear prepayment progress so a re-login never shows another customer's gauge
    try {
      prepaymentProgressStore.reset();
    } catch (err) {
      console.error("Error resetting prepayment progress store:", err);
    }

    // 4.6. Clear isAuthenticated cookie (httpOnly=false, so client can clear it)
    // This ensures cookie is cleared immediately, not waiting for server Set-Cookie response
    try {
      useCookie("isAuthenticated").value = null;
    } catch {}

    // 5. Reset internal state tracking
    cldUserLoadedAt.value = 0;
    cldUserLoadPromise = null;

    // 6. Reset user object to empty state
    user.value = {
      _id: "",
      userId: "",
      categories: [],
      tours: [],
      newsLetterSeen: false,
      meta: {
        apiKey: {
          hashedKey: "",
          prefix: "",
          name: "",
          createdAt: null,
          expiresAt: null,
          permissions: [],
          lastUsed: null,
          isActive: false,
        },
        apiKeys: [
          {
            hashedKey: "",
            prefix: "",
            name: "",
            createdAt: null,
            expiresAt: null,
            permissions: [],
            lastUsed: null,
            isActive: false,
          },
        ],
      },
    };

    // 7. Reset cldUser object to empty state
    cldUser.value = {
      cemail2: "",
      cemailpdn: "",
      cemailwbo: "",
      chain: "",
      email: "",
      emailShp: "",
      id: "",
      idCompta: "",
      name: "",
      role: "",
      category: "",
      callbackUrl: "",
      hmacSign: "",
      iTerms: "",
      cfranco: 0,
      canPayOnline: false,
      factoring: false,
      roles: [],
    };

    // 8. Clear categories preference
    categoriesPreference.value = {};

    // 8.5. Clear licenses and brands preferences
    licensesPreference.value = {};
    brandsPreference.value = {};
    parentLicenses.value = [];
    parentBrands.value = [];

    // 9. Clear user contacts data
    userContactsData.value = {
      contacts: [],
      addresses: [],
      crmContacts: [],
      crmContactSigs: [],
    };

    // 10. Clear isAuthenticated cookie
    try {
      useCookie("isAuthenticated", { path: "/" }).value = null;
    } catch {}

    console.log("[UserStore] All user state cleared");
  };

  const userContactsData = ref({
    contacts: [],
    addresses: [],
    crmContacts: [],
    crmContactSigs: [],
  });
  const pending = ref(false);
  const preSignupData = ref();
  const contactEmail = ref();
  const preSignupVatData = ref({});
  const signupError = ref({ value: false, message: "" });
  const loginRes = ref({ value: false, message: "" });
  const preSignupResponse = ref();
  const existingEmail = ref(false);
  const postSignupResponse = ref();
  const categoriesPreference = ref({});
  const licensesPreference = ref({});
  const brandsPreference = ref({});
  const parentLicenses = ref([]);
  const parentBrands = ref([]);
  const isPurchaseOrderRequired = ref(false);
  // Usefetch for login

  const loginViaH3 = async ({ userName, password, token }) => {
    loginRes.value = { value: false, message: "" };

    // IMPORTANT: Clear ALL previous user state before login attempt
    clearAllUserState();
    isAuthenticated.value = false;

    const { error, data } = await useFetch("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ userName, password, token }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (error.value) {
      loginRes.value.message = error.value.data?.data?.label;
    } else {
      loginRes.value.message = data.value.label;
      if (data && data.value && data.value?.success) {
        // nuxtStorage.localStorage.setData(
        //   "token",
        //   "access_token bearer 09022122xxx",
        //   12,
        //   "h"
        // );
        // TODO add init mercatore-engine on CLD side
        // nuxtStorage.localStorage.setData(
        //   "refresh_token",
        //   data.value.refresh_token,
        //   24,
        //   "h"
        // );
        isAuthenticated.value = true;
        loginRes.value.value = data.value.success;
        // Record when this token expires so the silent refresh runs shortly before that,
        // not on the first tab focus after login.
        auth.setIsAuthenticated(ACCESS_TOKEN_EXPIRY_SECONDS);

        // Do NOT hydrate from previous local cache here; it can briefly show the wrong user
        // if the cache belongs to a different account or the follow-up fetch fails.
        try {
          await Promise.allSettled([getCldUserData(true), getUserData(true)]);
        } catch {
          // non-blocking: UI can continue even if CLD user fetch fails
        }
      }
    }
  };
  const logout = async () => {
    // IMPORTANT: Clear ALL user state immediately
    clearAllUserState();
    isAuthenticated.value = false;
    
    try {
      await useFetch("/api/auth/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      // Logout API call failed, but state is already cleared
    }
  };
  const preSignup = async (payload) => {
    pending.value = true;
    const url = useCldURLs[CLD_ENV].auth.pre_register;
    // const url = useURLs[CLD_ENV].CLD_PREREGISTOR;
    const { data, error } = await useFetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (error.value && error.value.data) {
      preSignupResponse.value = error.value.data;
    } else {
      preSignupResponse.value = data.value;
    }
    pending.value = false;
    // nuxtStorage.localStorage.setData("pre-signup", data.value.data, 100, "h");
  };
  const sendContactEmailCld = async ({
    email,
    message,
    phone,
    name,
    token,
  }) => {
    const turnStileResponse = await validateNSendContact({
      token,
      email,
      message,
      phone,
      name,
    });
    contactEmail.value = turnStileResponse;
    // const { data } = await sendContactEmail({ email, message, phone, name });
    // contactEmail.value = data.value?.data;
  };

  const getPreSignup = async (id) => {
    pending.value = true;
    const url = `${useCldURLs[CLD_ENV].auth.pre_register}?id=${id}&serviceId=${id}`;
    const { data } = await useFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    pending.value = false;
    if (data.value?.status) {
      const {
        data: { client, vatData },
      } = data.value;
      // console.log("client ", client);
      preSignupData.value = client;
      preSignupVatData.value = vatData;
    } else {
      signupError.value = {
        value: true,
        message: data.value.label,
      };
    }

    // if (data.value.label === "ALREADY_VERIFIED") {
    //   signupError.value = {
    //     value: true,
    //     message: "ALREADY_VERIFIED",
    //   };
    // } else if (data.value.label === "INVALID_UNIQUE_ID") {
    //   signupError.value = {
    //     value: true,
    //     message: "INVALID_UNIQUE_ID",
    //   };
    // }
  };
  const emailExists = async (email) => {
    pending.value = true;
    const url = `${useCldURLs[CLD_ENV].utility.email_exists}?serviceId=emailchecknoid&email=${email}`;

    const { data } = await useFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    existingEmail.value = data.value;
    pending.value = false;
    return data;
  };

  /**
   * Check if VAT number exists and is valid against VIES database
   * @param {string} vat - The VAT number to validate
   * @param {string} countryCode - The country code (e.g., "BE", "FR")
   * @returns {Promise<Ref<import('../types/vat').VatValidationResponse | null>>}
   */
  const CheckVatExistAndisValid = async (vat, countryCode) => {
    pending.value = true;
    const url = `${useCldURLs[CLD_ENV].utility.check_vat}?countryCode=${countryCode}&vat=${vat}&id=${vat}`;

    // const url = `${useURLs[CLD_ENV].CHECK_VAT_EXIST_AND_ISVALID}?countryCode=${countryCode}&vat=${vat}`;
    const { data } = await useFetch(url, {
      key: vat,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    pending.value = false;
    return data;
  };
  const postSignup = async (payload) => {
    pending.value = true;
    const url = useCldURLs[CLD_ENV].auth.post_register;
    const { data, error } = await useFetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });

    pending.value = false;
    if (!data.value && error.value && error.value.data) {
      postSignupResponse.value = error.value.data;
    } else {
      postSignupResponse.value = data.value;
    }
  };
  const setSignupError = (v) => {
    signupError.value = v || {
      value: true,
      message: "ERROR_SIGNUP",
    };
  };
  const saveUserCategoryPrefrence = async (payload) => {
    const data = await saveUser(payload);
    return data;
  };
  const updateUserCategoryPrefrence = async (payload) => {
    const data = await updateUserCategories(payload);
    return data;
  };
  const saveUserLicensePrefrence = async (payload) => {
    const data = await saveUser(payload);
    return data;
  };
  const updateUserLicensePrefrence = async (payload) => {
    const { $fetch } = useNuxtApp();
    const data = await $fetch('/api/users/licenses', { method: 'PATCH', body: payload });
    return { value: { data } };
  };
  const saveUserBrandPrefrence = async (payload) => {
    const data = await saveUser(payload);
    return data;
  };
  const updateUserBrandPrefrence = async (payload) => {
    const { $fetch } = useNuxtApp();
    const data = await $fetch('/api/users/brands', { method: 'PATCH', body: payload });
    return { value: { data } };
  };
  const saveOrUpdateUser = async (payload) => {
    if (user.value._id) {
      // update

      const resp = await updateUser(payload);
      if (resp && resp.value && resp.value?.data) {
        user.value = resp.value.data;
        useNuxtApp().$showToast({
          msg: t("save_success.desc"),
          type: "success",
        });
      } else {
        useNuxtApp().$showToast({
          msg: t("save_fail.changes_not_saved"),
          type: "error",
        });
      }
    } else {
      const resp = await saveUser(payload);

      if (resp && resp.value && resp.value?.data) {
        useNuxtApp().$showToast({
          msg: t("save_success.desc"),
          type: "success",
        });
      } else {
        useNuxtApp().$showToast({
          msg: t("save_fail.changes_not_saved"),
          type: "error",
        });
      }
    }
  };
  const updateUserNewsLetterStatus = async ({
    _id,
    newsLetterSeen,
    userId,
  }) => {
    const url = useURLs[NODE_ENV].UPDATE_USER_NEWSLETTER_STATUS;
    const { data } = await useFetchApi(url, {
      key: _id,
      method: "PATCH",
      body: { _id, newsLetterSeen, userId },
      headers: {
        client_id: useURLs[NODE_ENV].CLIENT_ID,
        client_secret: useURLs[NODE_ENV].CLIENT_SECERET,
        "Content-Type": "application/json",
      },
      retry: 1,
    });
    return data;
  };
  const generateApiKey = async ({ _id, apiKey, userId }) => {
    const url = useURLs[NODE_ENV].GENERATE_API_KEY;
    const { data } = await useFetchApi(url, {
      key: `add ${apiKey.name}`,
      method: "PATCH",
      body: { _id, apiKey, userId: userId },
      headers: {
        client_id: useURLs[NODE_ENV].CLIENT_ID,
        client_secret: useURLs[NODE_ENV].CLIENT_SECERET,
        "Content-Type": "application/json",
      },
      retry: 1,
    });

    user.value = data.value;
    return data;
  };

  const removeApiKey = async ({ _id, apiKey }) => {
    const url = useURLs[NODE_ENV].REMOVE_API_KEY;
    const { data } = await useFetchApi(url, {
      key: `remove ${apiKey.name}`,
      method: "PATCH",
      body: { _id, apiKey },
      headers: {
        client_id: useURLs[NODE_ENV].CLIENT_ID,
        client_secret: useURLs[NODE_ENV].CLIENT_SECERET,
        "Content-Type": "application/json",
      },
      retry: 1,
    });

    user.value = data.value;
    return data;
  };
  const getUserData = async (forceExecute = false) => {
    if (!isAuthenticated.value || (user.value?._id && forceExecute !== true)) {
      return;
    }
    const data = await getUser();
    if (data?.value && data.value?.data) {
      user.value = data.value.data;
      const category = Object.assign(
        {},
        ...data.value.data.categories.map((x) => ({ [x.key]: x }))
      );

      categoriesPreference.value = category;
      // console.log("categoriesPreference.value", categoriesPreference.value);

      // Handle licenses preferences
      if (data.value.data.licenses && data.value.data.licenses.length > 0) {
        const licenses = Object.assign(
          {},
          ...data.value.data.licenses.map((x) => ({ [x.key]: x }))
        );
        licensesPreference.value = licenses;
      }

      // Handle brands preferences
      if (data.value.data.brands && data.value.data.brands.length > 0) {
        const brands = Object.assign(
          {},
          ...data.value.data.brands.map((x) => ({ [x.key]: x }))
        );
        brandsPreference.value = brands;
      }
    }
    return user;
  };

  const getCldUserData = async (forceExecute = false) => {
    if (!isAuthenticated.value) {
      return;
    }

    // Avoid duplicate concurrent calls
    if (cldUserLoadPromise) {
      return cldUserLoadPromise;
    }

    const now = Date.now();
    const isFresh =
      !!cldUser.value?.id &&
      !!cldUserLoadedAt.value &&
      now - cldUserLoadedAt.value < CLD_USER_REFRESH_MS;

    if (!forceExecute && isFresh) {
      return cldUser.value;
    }

    cldUserLoadPromise = (async () => {
      const data = await getCldUser();
      if (data.value) {
        cldUser.value = data.value.data;
        cldUserLoadedAt.value = Date.now();
        // NOTE: Removed localStorage caching to prevent stale user data issues
        // User data is now always fetched fresh from the API
      }
      return cldUser.value;
    })().finally(() => {
      cldUserLoadPromise = null;
    });

    return cldUserLoadPromise;
  };

  const getContactData = async () => {
    if (!isAuthenticated.value) {
      return;
    }
    const data = await getCldContactData();
    if (data.value) {
      userContactsData.value = data.value;
    }
  };

  const fetchAvailableLicenses = async () => {
    try {
      console.log('[STORE] fetchAvailableLicenses starting...');
      const data = await getAvailableLicenses();
      console.log('[STORE] fetchAvailableLicenses got data:', data);
      console.log('[STORE] data type:', typeof data);
      console.log('[STORE] data.value:', data?.value);
      console.log('[STORE] data.value.data:', data?.value?.data);
      console.log('[STORE] Array.isArray(data.value.data):', Array.isArray(data?.value?.data));

      // The API returns {data: [...]} so we need to extract it
      if (data?.value && data.value.data && Array.isArray(data.value.data)) {
        parentLicenses.value = data.value.data;
        console.log('[STORE] ✅ parentLicenses successfully set to:', parentLicenses.value.length, 'items');
      } else {
        console.warn('[STORE] ⚠️ Unexpected licenses response format:', data);
        parentLicenses.value = [];
      }
    } catch (error) {
      console.error('[STORE] ❌ Error fetching available licenses:', error);
      parentLicenses.value = [];
    }
  };

  const fetchAvailableBrands = async () => {
    try {
      console.log('[STORE] fetchAvailableBrands starting...');
      const data = await getAvailableBrands();
      console.log('[STORE] fetchAvailableBrands got data:', data);
      console.log('[STORE] data type:', typeof data);
      console.log('[STORE] data.value:', data?.value);
      console.log('[STORE] data.value.data:', data?.value?.data);
      console.log('[STORE] Array.isArray(data.value.data):', Array.isArray(data?.value?.data));

      // The API returns {data: [...]} so we need to extract it
      if (data?.value && data.value.data && Array.isArray(data.value.data)) {
        parentBrands.value = data.value.data;
        console.log('[STORE] ✅ parentBrands successfully set to:', parentBrands.value.length, 'items');
      } else {
        console.warn('[STORE] ⚠️ Unexpected brands response format:', data);
        parentBrands.value = [];
      }
    } catch (error) {
      console.error('[STORE] ❌ Error fetching available brands:', error);
      parentBrands.value = [];
    }
  };

  const getIsPORequired = async () => {
    const data = await getIsPurchaseOrderRequired();
    if (data && data.value) {
      isPurchaseOrderRequired.value = data.value.data;
    }
  };
  const updateCldUserEmails = async (payload) => {
    const data = await updateUserEmails(payload);
    console.log("updateUserEmails Data", data.value);
    return data;
  };
  const setIsAuthenticated = () => {
    const cookieAuth = !!useCookie("isAuthenticated").value;
    isAuthenticated.value = cookieAuth;
    
    if (cookieAuth) {
      // Only fetch fresh data - never hydrate from potentially stale storage
      getCldUserData(true);
      getUserData(true);
    } else {
      // Not authenticated - clear everything
      clearAllUserState();
    }
  };
  const removeIsAuthenticated = () => {
    // Use the single source of truth for clearing state
    clearAllUserState();
    isAuthenticated.value = false;
  };

  const sendCredentialsEmail = async ({ id, uniqueID }) => {
    try {
      //TODO add turnStileToken
      const turnStileToken = true;
      if (turnStileToken && id && uniqueID) {
        const url = `${useURLs[CLD_ENV].SEND_CREDENTIALS_EMAIL}?id=${id}&uniqueID=${uniqueID}`;
        const { data } = await useFetch(url, {
          key: uniqueID,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (data.value?.data?.code == 200) {
          useNuxtApp().$showToast({
            msg: t("EMAIL_SENT"),
            type: "info",
          });
        } else {
          useNuxtApp().$showToast({
            msg: t("email_credentials_error"),
            type: "info",
          });
        }
        return data;
      }
    } catch (error) {
      console.log("error", error);
    }
    return false;
  };
  const forgotPassword = async ({ email, selectedLanguage, token }) => {
    try {
      const url = "/api/auth/forgot-password";

      const responseData = await $fetch(url, {
        method: "POST",
        body: { email, selectedLanguage, token },
      });

      // Handle success response - don't show toast here, let the component handle it
      // This prevents duplicate messages
      return true;
    } catch (err) {
      console.error("💥 Forgot Password Exception:", err);
      
      // Extract error message from the error response
      let errorMessage = "An unexpected error occurred";
      
      if (err.data?.statusMessage) {
        errorMessage = err.data.statusMessage;
      } else if (err.statusMessage) {
        errorMessage = err.statusMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Add status code info
      const statusCode = err.status || err.statusCode;
      if (statusCode) {
        errorMessage = `[${statusCode}] ${errorMessage}`;
      }
      
      useNuxtApp().$showToast({
        msg: errorMessage,
        type: "error",
      });
      return false;
    }
  };
  const resetPassword = async ({ guid, newPassword, selectedLanguage }) => {
    const url = `/api/auth/reset-password`;
    const { data } = await useFetch(url, {
      key: guid,
      method: "POST",
      body: JSON.stringify({
        guid,
        newPassword,
        selectedLanguage,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (data.value?.success) {
      useNuxtApp().$showToast({
        msg: data.value?.message || "Password reset successfully",
        type: "success",
      });
    } else {
      useNuxtApp().$showToast({
        msg: data.value?.message || "Invalid or expired reset link",
        type: "error",
      });
    }
    return data.value?.success;
  };

  const validateCallBackUrl = async (formData) => {
    const url = useCldURLs[CLD_ENV].user.validate_callback_url;
    const { data } = await useFetchApi(
      url,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: formData,
      },
      true
    );
    callbackUrlResponseApi.value = data.value;
  };
  const updateCallbackUrl = async (cbUrl) => {
    const url = useCldURLs[CLD_ENV].user.callback_url;
    const { data } = await useFetchApi(
      url,
      {
        key: cbUrl,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: { callbackUrl: cbUrl },
      },
      true
    );

    cldUser.value.callbackUrl = data.value;
  };
  const updateShaMac = async () => {
    const url = useCldURLs[CLD_ENV].user.sha_secret;
    const { data } = await useFetchApi(
      url,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: {},
      },
      true
    );

    hmac.value = data.value;
  };

  const cliSetting = ref({
    orderEmailThresholdEnable: false,
    orderEmailThresholdEur: 0,
  });

  const normalizeCliSetting = (setting) => {
    const source = setting || {};
    return {
      orderEmailThresholdEnable:
        source.orderEmailThresholdEnable ??
        source.OrderEmailThresholdEnable ??
        false,
      orderEmailThresholdEur:
        Number(source.orderEmailThresholdEur ?? source.OrderEmailThresholdEur ?? 0) ||
        0,
    };
  };

  const getCliSetting = async () => {
    try {
      const { getCliSetting: getCliSettingApi } = await import('../api/user/index.js');
      const data = await getCliSettingApi();
      const payload = data?.value?.data ?? data?.value;
      cliSetting.value = normalizeCliSetting(payload);
      return data;
    } catch (error) {
      console.error("Error fetching CLI setting:", error);
      return null;
    }
  };

  const updateCliSetting = async (payload) => {
    try {
      const { updateCliSetting: updateCliSettingApi } = await import('../api/user/index.js');
      const data = await updateCliSettingApi(payload);
      const responsePayload = data?.value?.data ?? data?.value ?? payload;
      cliSetting.value = normalizeCliSetting(responsePayload);
      return data;
    } catch (error) {
      console.error("Error updating CLI setting:", error);
      return null;
    }
  };

  // Initial data fetch on store creation - only if authenticated
  // Do NOT hydrate from localStorage cache - always fetch fresh to prevent stale data
  if (isAuthenticated.value) {
    getCldUserData(true);
    getUserData(true);
  }

  watch(
    isAuthenticated,
    (v) => {
      if (v) {
        // Only fetch fresh data from API - do NOT hydrate from potentially stale storage
        getCldUserData(true);
        getUserData(true);
      } else {
        // When auth becomes false, ensure state is cleared
        clearAllUserState();
      }
    },
    { immediate: false }
  );
  return {
    pending,
    user,
    cldUser,
    categoriesPreference,
    licensesPreference,
    brandsPreference,
    parentLicenses,
    parentBrands,
    existingEmail,
    emailExists,
    CheckVatExistAndisValid,
    saveUserCategoryPrefrence,
    updateUserCategoryPrefrence,
    saveUserLicensePrefrence,
    updateUserLicensePrefrence,
    saveUserBrandPrefrence,
    updateUserBrandPrefrence,
    saveOrUpdateUser,
    updateUserNewsLetterStatus,
    getUserData,
    getCldUserData,
    updateCldUserEmails,
    sendCredentialsEmail,
    logout,
    isAuthenticated,
    loginRes,
    getPreSignup,
    preSignup,
    postSignup,
    preSignupData,
    preSignupVatData,
    signupError,
    setSignupError,
    preSignupResponse,
    postSignupResponse,
    sendContactEmailCld,
    contactEmail,
    setIsAuthenticated,
    removeIsAuthenticated,
    getIsPORequired,
    isPurchaseOrderRequired,
    forgotPassword,
    resetPassword,
    loginViaH3,
    generateApiKey,
    removeApiKey,
    validateCallBackUrl,
    callbackUrlResponseApi,
    updateCallbackUrl,
    updateShaMac,
    hmac,
    getContactData,
    userContactsData,
    fetchAvailableLicenses,
    fetchAvailableBrands,
    cliSetting,
    getCliSetting,
    updateCliSetting,
  };
});
